<template>
	<div class="banner-nav" :class="{ extended: heroBannerList.length === 4 }">
		<div ref="preview-list" class="preview-list">
			<div
				v-for="(banner, $index) in heroBannerList"
				:key="$index"
				ref="previews"
				class="preview"
				:class="{ selected: selectedIndex === $index, userStoppedProgress: userStoppedProgress }"
				@click="selectBanner($index, true, true)"
			>
				<div class="preview-contents">
					<!-- <div class="preview-logo" :style="{ backgroundImage: `url(${cdnHost}${banner.bannerImage})` }"></div> -->
					<div class="preview-logo"><cdnImage :src="banner.bannerImage" /></div>
					<div>
						<div class="preview-name">{{ banner.productName }}</div>
						<div class="preview-title">{{ getLocaleString(banner.title) }}</div>
					</div>
				</div>
				<div v-if="selectedIndex === $index" class="timer-bar-bg" :class="{ userStoppedProgress: userStoppedProgress }"></div>
				<div v-if="selectedIndex === $index" class="timer-bar" :style="{ width: timerBarWidthPct }"></div>
			</div>
		</div>
		<div
			v-if="heroBannerList.length > 4"
			class="desktop arrow previous"
			:class="{ nextHovered: nextHovered, nextClicked: nextClicked, disabled: canNavNext }"
			@click="!canNavNext ? desktopArrowClick('previous') : null"
		></div>
		<div
			v-if="heroBannerList.length > 4"
			class="desktop arrow next"
			:class="{ disabled: !canNavNext }"
			@click="canNavNext ? desktopArrowClick('next') : null"
			@mouseenter="nextHovered = true"
			@mouseleave="[(nextHovered = false), (nextClicked = false)]"
			@mousedown="nextClicked = true"
			@mouseup="nextClicked = false"
		></div>
		<div class="mobile arrow previous" @click="previousMobileArrowClick"></div>
		<div class="mobile arrow next" @click="nextMobileArrowClick"></div>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { getLocaleString } from '../../../plugins/helpers';
import cdnImage from '@/components/cdn-image';

const TOTAL_INTERVAL_TIME_MS = 5 * 1000,
	INTERVAL_TIME_MS = 10;

export default {
	name: 'BannerNav',
	components: { cdnImage },
	props: {
		selectedIndex: {
			type: Number,
			default: 0,
		},
		hovered: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			timerInterval: null,
			timerBarWidth: 0,
			userStoppedProgress: false,
			changingIndex: false,
			nextHovered: false,
			nextClicked: false,
			canNavNext: true,
		};
	},
	computed: {
		...mapState(['heroBannerList', 'isIOS', 'isAndroid']),
		timerBarWidthPct() {
			return this.timerBarWidth + '%';
		},
		timerBarIncreaseBy() {
			return (INTERVAL_TIME_MS / TOTAL_INTERVAL_TIME_MS) * 100;
		},
		language() {
			return this.$i18n.locale;
		},
		lastPageLength() {
			return this.heroBannerList.length - 4;
		},
		isMobile() {
			return this.isIOS || this.isAndroid;
		},
	},
	mounted() {
		this.startTimer();
		this.$nextTick(() => {
			window.addEventListener('resize', this.handleResize);
		});

		this.$root.$on('swipeLeft', () => {
			this.nextMobileArrowClick();
		});
		this.$root.$on('swipeRight', () => {
			this.previousMobileArrowClick();
		});
	},
	destroyed() {
		window.removeEventListener('resize', this.handleResize);
		if (this.timerInterval) clearInterval(this.timerInterval);
	},
	methods: {
		handleResize() {
			setTimeout(() => {
				this.$refs['preview-list'].scrollLeft = this.canNavNext ? 0 : 9999;
			}, 250);
		},
		selectBanner(index, stopTimer, jump) {
			if (this.changingIndex) return;
			this.timerBarWidth = 0;
			this.$emit('setIndex', index);
			if (jump) {
				dataLayer.push({
					event: 'Hero Navigation',
					heroGameName: this.heroBannerList[index].productName,
					heroGameID: this.heroBannerList[index].productId,
					heroNavigationType: 'Jump',
				});
			}
			if ((this.canNavNext && index >= 4) || (!this.canNavNext && index < this.lastPageLength)) {
				this.scrollBannerNav();
			}
			if (stopTimer) {
				this.userStopProgress();
				this.changingIndex = true;
				setTimeout(() => {
					this.changingIndex = false;
				}, 650);
			} else {
				this.startTimer();
			}
		},
		previousMobileArrowClick() {
			if (this.changingIndex) return;
			const newIndex = this.selectedIndex === 0 ? this.heroBannerList.length - 1 : this.selectedIndex - 1;
			dataLayer.push({
				event: 'Hero Navigation',
				heroGameName: this.heroBannerList[newIndex].productName,
				heroGameID: this.heroBannerList[newIndex].productId,
				heroNavigationType: `Mobile Left Arrow`,
			});
			this.selectBanner(newIndex, true);
		},
		nextMobileArrowClick() {
			if (this.changingIndex) return;
			const newIndex = this.selectedIndex >= this.heroBannerList.length - 1 ? 0 : this.selectedIndex + 1;
			dataLayer.push({
				event: 'Hero Navigation',
				heroGameName: this.heroBannerList[newIndex].productName,
				heroGameID: this.heroBannerList[newIndex].productId,
				heroNavigationType: `Mobile Right Arrow`,
			});
			this.selectBanner(newIndex, true);
		},
		desktopArrowClick(direction) {
			if (this.changingIndex) return;
			dataLayer.push({
				event: 'Hero Navigation',
				heroGameName: this.heroBannerList[this.selectedIndex].productName,
				heroGameID: this.heroBannerList[this.selectedIndex].productId,
				heroNavigationType: `Desktop ${direction === 'next' ? 'Right' : 'Left'} Arrow`,
			});
			this.scrollBannerNav(direction);
		},
		scrollBannerNav(direction) {
			const previewList = this.$refs['preview-list'];
			if (!previewList) return;
			let scrollLeft = 0,
				canNavNext = true;
			if ((direction && direction === 'next') || previewList.scrollLeft === 0) {
				scrollLeft = 9999;
				canNavNext = false;
			}
			previewList.scrollLeft = scrollLeft;
			this.canNavNext = canNavNext;
		},
		selectNextBanner() {
			const newIndex = this.selectedIndex >= this.heroBannerList.length - 1 ? 0 : this.selectedIndex + 1;
			this.selectBanner(newIndex);
		},
		userStopProgress() {
			this.userStoppedProgress = true;
			if (this.timerInterval) clearInterval(this.timerInterval);
		},
		startTimer() {
			this.timerInterval = setInterval(this.progress, INTERVAL_TIME_MS);
		},
		progress() {
			if (this.hovered && !this.isMobile) return;
			if (this.timerBarWidth >= 100) {
				clearInterval(this.timerInterval);
				this.selectNextBanner();
			} else {
				this.timerBarWidth += this.timerBarIncreaseBy;
			}
		},
		getLocaleString(data) {
			return getLocaleString(data, this.language);
		},
	},
};
</script>

<style lang="less" scoped>
.banner-nav {
	bottom: 0;
	height: 6px;
	padding: 0 20px;
	position: absolute;
	width: 100%;
	z-index: 10;
	.preview-list {
		align-items: center;
		display: flex;
		height: 100%;
		justify-content: center;
		margin: auto;
		scroll-behavior: smooth;
		width: 335px;
		.preview {
			background-color: rgba(255, 255, 255, 0.3);
			cursor: pointer;
			flex: 1;
			height: 4px;
			overflow: hidden;
			transition: all 0.2s;
			&:not(:last-child) {
				margin-right: 3px;
			}
			&.selected {
				background-color: rgba(0, 119, 255, 0.5);
				height: 6px;
				&.userStoppedProgress {
					background-color: #0077ff;
				}
			}
			.preview-contents {
				display: none;

				.preview-logo {
					position: relative;
					overflow: hidden;
				}
			}
			.timer-bar {
				background-color: #0077ff;
				height: 100%;
				transition: all linear;
				width: 0;
			}
			.time-bar-bg {
				display: none;
			}
		}
	}
	.arrow {
		background-color: rgba(0, 0, 0, 0.5);
		border: 1px solid rgba(255, 255, 255, 0.2);
		bottom: 231px;
		cursor: pointer;
		height: 60px;
		position: absolute;
		user-select: none;
		transition: all 0.2s;
		width: 42px;
		&.desktop {
			display: none;
		}
		&:not(:active):not(.disabled):hover {
			background-color: rgba(0, 0, 0, 1);
			border: 1px solid rgba(255, 255, 255, 0.7);
			&:before {
				opacity: 1;
			}
		}
		&:not(.disabled):active {
			&:before {
				opacity: 0.5;
			}
		}
		&:before {
			background-position: center;
			background-repeat: no-repeat;
			content: '';
			height: 100%;
			opacity: 0.8;
			position: absolute;
			width: 100%;
		}
		&.previous {
			&:before {
				background-image: url('~@/images/hero-banner/arrows/previous.svg');
			}
			left: 0;
		}
		&.next {
			&:before {
				background-image: url('~@/images/hero-banner/arrows/next.svg');
			}
			right: 0;
		}
	}
}

@media (min-width: 548px) {
	.banner-nav {
		.preview-list {
			width: 507px;
		}
	}
}

@media (min-width: 768px) {
	.banner-nav {
		height: 7px;
		.preview-list {
			.preview {
				flex: inherit;
				height: 3px;
				width: 54px;
				&.selected {
					height: 7px;
					width: 77px;
				}
				&:not(:last-child) {
					margin-right: 5px;
				}
			}
		}
		.arrow {
			bottom: 315px;
		}
	}
}

@media (min-width: 1024px) {
	.banner-nav {
		.arrow {
			bottom: 100px;
		}
	}
}

@media (min-width: 1280px) {
	.banner-nav {
		height: 80px;
		left: 0;
		margin: auto;
		padding: 0 27px 0 0;
		right: 0;
		width: 961px;
		&.extended {
			padding: 0;
			.preview-list {
				width: 961px;
				.preview {
					min-width: 239px;
					&.selected {
						width: 239px;
					}
				}
			}
		}
		.preview-list {
			justify-content: flex-start;
			overflow: hidden;
			width: 934px;
			.preview {
				background-color: rgba(46, 46, 50, 0.5);
				height: 100%;
				min-width: 232px;
				position: relative;
				&:not(:last-child) {
					margin-right: 2px;
				}
				&:hover {
					background-color: rgba(105, 108, 113, 0.5);
				}
				&.selected {
					background-color: rgba(105, 108, 113, 0.5);
					height: 100%;
					width: 232px;
					&.userStoppedProgress {
						background-color: rgba(105, 108, 113, 0.5);
					}
					.preview-contents {
						.preview-name {
							color: rgba(255, 255, 255, 0.7);
						}
						.preview-title {
							color: white;
						}
					}
				}
				.preview-contents {
					display: flex;
					.preview-logo {
						background-position: center;
						background-size: cover;
						height: 54px;
						margin: 13px 0 13px 14px;
						min-width: 54px;
						padding: 0;
						width: 54px;
					}
					> div {
						padding: 13px 8px;
					}
					.preview-name {
						color: rgba(255, 255, 255, 0.5);
						font-family: Roboto;
						font-size: 10px;
						margin-bottom: 5px;
					}
					.preview-title {
						color: rgba(255, 255, 255, 0.8);
						font-family: Roboto;
						font-size: 12px;
						line-height: 1.25;
					}
				}
				.timer-bar {
					bottom: 0;
					height: 5px;
					position: absolute;
				}
				.timer-bar-bg {
					background-color: rgba(255, 255, 255, 0.1);
					bottom: 0;
					display: block;
					height: 5px;
					position: absolute;
					width: 100%;
					&.userStoppedProgress {
						background-color: #0077ff;
						width: 100%;
					}
				}
			}
		}
		.arrow {
			bottom: 0;
			height: 40px;
			right: 0;
			width: 27px;
			&.disabled {
				cursor: default;
				&:before {
					opacity: 0.2;
				}
			}
			&.mobile {
				display: none;
			}
			&.desktop {
				display: block;
			}
			&.previous {
				&:before {
					background-image: url('~@/images/hero-banner/arrows/previous_1280.svg');
				}
				&:not(.nextClicked).disabled.nextHovered {
					border-bottom: 1px solid rgba(255, 255, 255, 0.7);
				}
				bottom: 40px;
				left: auto;
			}
			&.next {
				&:before {
					background-image: url('~@/images/hero-banner/arrows/next_1280.svg');
				}
				border-top: none !important;
				left: auto;
			}
		}
	}
}

@media (min-width: 1440px) {
	.banner-nav {
		height: 90px;
		padding: 0 33px 0 0;
		width: 1080px;
		&.extended {
			.preview-list {
				width: 1080px;
				.preview {
					min-width: 270px;
					&.selected {
						width: 270px;
					}
				}
			}
		}
		.preview-list {
			width: 1047px;
			.preview {
				min-width: 261px;
				&:not(:last-child) {
					margin-right: 1px;
				}
				&.selected {
					width: 261px;
				}
				.preview-contents {
					.preview-logo {
						height: 60px;
						margin: 16px 0 15px 15px;
						min-width: 60px;
						width: 60px;
					}
					> div {
						padding: 20px 11px;
					}
					.preview-name {
						font-size: 12.5px;
					}
					.preview-title {
						font-size: 13.5px;
					}
				}
			}
		}
		.arrow {
			height: 45px;
			width: 33px;
			&.previous {
				bottom: 45px;
			}
		}
	}
}

@media (min-width: 1600px) {
	.banner-nav {
		height: 100px;
		padding: 0 36px 0 0;
		width: 1199px;
		&.extended {
			.preview-list {
				width: 1200px;
				.preview {
					min-width: 300px;
					&.selected {
						width: 300px;
					}
				}
			}
		}
		.preview-list {
			width: 1164px;
			.preview {
				min-width: 290px;
				&.selected {
					width: 290px;
				}
				.preview-contents {
					.preview-logo {
						height: 67px;
						margin: 17px 0 17px 17px;
						min-width: 67px;
						width: 67px;
					}
					> div {
						padding: 22px 13px;
					}
					.preview-name {
						font-size: 14px;
					}
					.preview-title {
						font-size: 16.5px;
					}
				}
			}
		}
		.arrow {
			height: 50px;
			width: 36px;
			&.previous {
				&:before {
					background-image: url('~@/images/hero-banner/arrows/previous_1600.svg');
				}
				bottom: 50px;
			}
			&.next {
				&:before {
					background-image: url('~@/images/hero-banner/arrows/next_1600.svg');
				}
			}
		}
	}
}

@media (min-width: 1920px) {
	.banner-nav {
		height: 120px;
		padding: 0 42px 0 0;
		width: 1440px;
		&.extended {
			.preview-list {
				width: 1440px;
				.preview {
					min-width: 360px;
					&.selected {
						width: 360px;
					}
				}
			}
		}
		.preview-list {
			width: 1398px;
			.preview {
				min-width: 349px;
				&.selected {
					width: 349px;
				}
				.preview-contents {
					.preview-logo {
						height: 80px;
						margin: 20px 0 20px 20px;
						min-width: 80px;
						width: 80px;
					}
					> div {
						padding: 28px 16px;
					}
					.preview-name {
						font-size: 13.5px;
					}
					.preview-title {
						font-size: 15.5px;
					}
				}
			}
		}
		.arrow {
			height: 60px;
			width: 42px;
			&.previous {
				&:before {
					background-image: url('~@/images/hero-banner/arrows/previous.svg');
				}
				bottom: 60px;
			}
			&.next {
				&:before {
					background-image: url('~@/images/hero-banner/arrows/next.svg');
				}
			}
		}
	}
}
</style>
